import { Component, DestroyRef, ElementRef, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MenuController, Platform } from '@ionic/angular';
import { Events } from 'src/app/shared/services/global/events.service';
import { GlobalService, AnalyticsService } from 'src/app/shared/services';
import { EventFieldsVisibility } from 'src/app/models/event-fields-visibility';
import { InAppBrowserService } from 'src/app/providers/in-app-browser/in-app-browser.service';
import { DateTimeService } from 'src/app/providers/date-time/date-time.service';
import { EventColors } from 'src/app/models/event-colors';
import { EventDataService } from 'src/app/shared/services/eventData/event-data.service';
import { ModuleDataService } from 'src/app/shared/services/module-data/module-data.service';
import { DaoEventsService } from 'src/app/providers/db/dao-events.service';
import { DaoSpeakersService } from 'src/app/providers/db/dao-speakers.service';
import { DaoScheduleService } from 'src/app/providers/db/dao-schedule.service';
import { DaoAttendeesService } from 'src/app/providers/db/dao-attendees.service';
import { DaoSponsorsService } from 'src/app/providers/db/dao-sponsors.service';
import { SponsorProps } from 'src/app/modules/list-sponsors/@types/sponsor';
import { TypeModule } from 'src/app/enums/type-module';
import { Subscription, firstValueFrom, interval } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface Countdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

type EventState = 'pre' | 'live' | 'post';

@Component({
  selector: 'app-event',
  templateUrl: './event.page.html',
  styleUrls: ['./event.page.scss'],
})
export class EventPage implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private menuCtrl = inject(MenuController);
  private global = inject(GlobalService);
  private eventsService = inject(Events);
  private analytics = inject(AnalyticsService);
  private dbEvent = inject(DaoEventsService);
  private inAppBrowser = inject(InAppBrowserService);
  private dt = inject(DateTimeService);
  private SEventData = inject(EventDataService);
  private platform = inject(Platform);
  private elementRef = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  private sanitizer = inject(DomSanitizer);
  private speakersDao = inject(DaoSpeakersService);
  private scheduleDao = inject(DaoScheduleService);
  private attendeesDao = inject(DaoAttendeesService);
  private sponsorsDao = inject(DaoSponsorsService);
  private SModuleData = inject(ModuleDataService);

  eventId: string = null;
  event: any = null;
  module: any = null;
  description: string = null;
  language: string = null;

  dayStart: string = null;
  dayEnd: string = null;
  monthStart: string = null;
  monthEnd: string = null;
  yearStart: string = null;
  yearEnd: string = null;
  formattedDateRange: string = null;

  isMobile = false;
  loader = true;
  backBtn = true;
  hasScheduleModule = false;
  scheduleModuleId: string | null = null;
  sponsors: SponsorProps[] = [];
  groupedSponsors: { category: string; items: SponsorProps[] }[] = [];

  countdown = signal<Countdown>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  speakersCount = signal<number | null>(null);
  sessionsCount = signal<number | null>(null);
  attendeesCount = signal<number | null>(null);
  statsLoaded = signal(false);

  eventColors: EventColors = new EventColors();
  subscriptions: Subscription[] = [];
  mapUrl: SafeResourceUrl | null = null;

  get eventState(): EventState {
    if (!this.event?.startDate) return 'pre';
    const now = new Date();
    const start = new Date(this.event.startDate);
    const end = new Date(this.event.endDate);
    if (now < start) return 'pre';
    if (now <= end) return 'live';
    return 'post';
  }

  get eventFields(): EventFieldsVisibility {
    return this.event?.eventFields || new EventFieldsVisibility();
  }

  constructor() {
    this.menuCtrl.enable(true);
    this.eventId = this.route.snapshot.params.eventId
      || this.route.snapshot.parent?.params?.eventId
      || localStorage.getItem('eventId');
    this.language = this.SEventData.getLanguage(true) || 'en_US';
  }

  ngOnInit() {
    if (this.platform.is('ios') || this.platform.is('android')) {
      this.isMobile = true;
    }

    this.loadEvent();
    this.checkScheduleModule();
    this.loadSponsors();

    this.dbEvent.getModuleEvent(this.eventId).then(m => this.module = m);

    this.analytics.moduleAccessEvent(this.eventId);

    if (this.global.previousPage === 'container') {
      this.backBtn = false;
    } else {
      this.backBtn = true;
    }

    this.eventsService.subscribe('menuBadge', () => {
      this.global.notificationBadge;
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private loadEvent() {
    const sub = this.SEventData.getEventDataListener(this.eventId).subscribe({
      next: (data: any) => {
        this.event = data;
        this.loader = false;

        if (!this.event.eventFields) {
          this.event.eventFields = new EventFieldsVisibility();
        }

        this.description = this.event.description?.[this.language]
          || this.event.description?.['en_US']
          || this.event.description?.[Object.keys(this.event.description || {})[0]]
          || null;

        this.eventColors = data.colors;
        this.applyTheme(this.eventColors);

        this.formatDates();

        if (this.event.placeAddress) {
          this.mapUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
            `https://www.google.com/maps?q=${encodeURIComponent(this.event.placeAddress)}&output=embed`
          );
        }

        this.loadStats();

        if (this.eventState === 'pre' && this.event.eventFields?.date) {
          this.startCountdown();
        }
      },
      error: () => {
        this.loader = false;
      }
    });

    this.subscriptions.push(sub);
  }

  private formatDates() {
    const start = new Date(this.event.startDate);
    const end = new Date(this.event.endDate);
    const locale = this.dt.deviceLocale || 'pt-BR';

    this.dayStart = start.getDate().toString().padStart(2, '0');
    this.dayEnd = end.getDate().toString().padStart(2, '0');
    this.monthStart = start.toLocaleString(locale, { month: 'long' });
    this.monthEnd = end.toLocaleString(locale, { month: 'long' });
    this.yearStart = start.getFullYear().toString();
    this.yearEnd = end.getFullYear().toString();

    if (this.monthStart === this.monthEnd && this.yearStart === this.yearEnd) {
      this.formattedDateRange = `${this.dayStart} a ${this.dayEnd} de ${this.monthStart} de ${this.yearStart}`;
    } else if (this.yearStart === this.yearEnd) {
      this.formattedDateRange = `${this.dayStart} de ${this.monthStart} a ${this.dayEnd} de ${this.monthEnd} de ${this.yearStart}`;
    } else {
      this.formattedDateRange = `${this.dayStart} de ${this.monthStart} de ${this.yearStart} a ${this.dayEnd} de ${this.monthEnd} de ${this.yearEnd}`;
    }
  }

  private applyTheme(colors: EventColors) {
    if (!colors) return;
    const host = this.elementRef.nativeElement as HTMLElement;
    host.style.setProperty('--ev-primary', colors.menu_color || '#201157');
    host.style.setProperty('--ev-primary-text', colors.menu_text_color || '#ffffff');
    host.style.setProperty('--ev-title', colors.title_color || '#201157');
    host.style.setProperty('--ev-body', colors.text_content_color || '#555555');
    host.style.setProperty('--ev-link', colors.link_color || '#201157');
    host.style.setProperty('--ev-link-text', colors.btn_text_color || '#ffffff');
    host.style.setProperty('--ev-card-bg', colors.bg_content_color || '#ffffff');
    host.style.setProperty('--ev-bg', colors.bg_general_color || '#f6f3ff');
    host.style.setProperty('--ev-accent', colors.active_menu_item || colors.link_color || '#EB266A');
  }

  private startCountdown() {
    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const now = Date.now();
        const target = new Date(this.event.startDate).getTime();
        const diff = Math.max(0, target - now);

        this.countdown.set({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000),
        });
      });
  }

  private async loadStats() {
    try {
      const [speakers, attendees] = await Promise.all([
        this.speakersDao.getSpeakersByEvent(this.eventId).catch(() => null),
        firstValueFrom(this.attendeesDao.fetchAttendeesByEvent(this.eventId)).catch(() => null),
      ]);

      if (speakers && Array.isArray(speakers)) this.speakersCount.set(speakers.length);
      if (attendees && Array.isArray(attendees)) this.attendeesCount.set(attendees.length);
    } catch {
    }
    this.statsLoaded.set(true);
  }

  openLink(url: string) {
    this.inAppBrowser.openLink(url);
  }

  goToSchedule() {
    if (!this.scheduleModuleId) return;
    this.router.navigate(['/event', this.eventId, 'schedule', this.scheduleModuleId]);
  }

  private checkScheduleModule() {
    this.SModuleData.getModuleId(this.eventId, TypeModule.SCHEDULE)
      .then(id => {
        this.hasScheduleModule = true;
        this.scheduleModuleId = id;
      })
      .catch(() => {
        this.hasScheduleModule = false;
      });
  }

  private async loadSponsors() {
    try {
      const list = await this.sponsorsDao.fetchSponsorsByEvent(this.eventId);
      this.sponsors = (list || []) as SponsorProps[];
      const cats = new Map<string, SponsorProps[]>();
      for (const s of this.sponsors) {
        const cat = s.category || 'Outros';
        if (!cats.has(cat)) cats.set(cat, []);
        cats.get(cat)!.push(s);
      }
      this.groupedSponsors = Array.from(cats.entries()).map(([category, items]) => ({ category, items }));
    } catch {
      this.sponsors = [];
    }
  }
}
